import { FC, useMemo, useState } from 'react';

import type { ActivityCounts, TeamActivityMember } from '@/api/gamma-team/types';
import {
  Badge,
  EmptyState,
  getTableMinWidth,
  Table,
  type TableColumn,
  type TableRow,
  theme,
  Typography,
} from '@/design-system';

import {
  DEFAULT_TEAM_SORT,
  memberName,
  nextTeamSort,
  sortTeamMembers,
  type TeamSortKey,
} from './team-sorting';

export const TOTAL_ROW_LABEL = 'Total';
export const FORMER_BADGE_LABEL = 'Former';

const COUNT_COLUMNS: { key: keyof ActivityCounts; header: string }[] = [
  { key: 'contactsReached', header: 'Contacts reached' },
  { key: 'entries', header: 'Entries' },
  { key: 'calls', header: 'Calls' },
  { key: 'messages', header: 'Messages' },
  { key: 'meetings', header: 'Meetings' },
  { key: 'notes', header: 'Notes' },
  { key: 'dealsCreated', header: 'Deals created' },
  { key: 'dealsAdvanced', header: 'Deals advanced' },
];

interface TeamTableRow extends TableRow {
  label: string;
  counts: ActivityCounts;
  former: boolean;
  isTotal: boolean;
}

const NameCell: FC<{ row: TeamTableRow }> = ({ row }) => (
  <Typography
    variant="body3"
    fontWeight={row.isTotal ? 600 : 500}
    color={theme.colors.text.primary}
    noWrap
  >
    {row.label}
    {row.former && (
      <>
        {' '}
        <Badge label={FORMER_BADGE_LABEL} color="default" variant="subtle" size="sm" />
      </>
    )}
  </Typography>
);

const CountCell: FC<{ row: TeamTableRow; column: keyof ActivityCounts }> = ({ row, column }) => (
  <Typography
    variant="body3"
    fontWeight={row.isTotal ? 600 : 400}
    color={theme.colors.text.primary}
  >
    {row.counts[column].toLocaleString()}
  </Typography>
);

const columns: TableColumn<TeamTableRow>[] = [
  {
    key: 'name',
    header: 'Team member',
    sortable: true,
    minWidth: 220,
    cell: (row) => <NameCell row={row} />,
  },
  ...COUNT_COLUMNS.map(
    ({ key, header }): TableColumn<TeamTableRow> => ({
      key,
      header,
      align: 'right',
      sortable: true,
      width: 130,
      cell: (row) => <CountCell row={row} column={key} />,
    }),
  ),
];

interface TeamTableProps {
  members: TeamActivityMember[];
  total: ActivityCounts | null;
  isLoading: boolean;
  onSelectMember: (_userId: string) => void;
}

export const TeamTable: FC<TeamTableProps> = ({ members, total, isLoading, onSelectMember }) => {
  const [sort, setSort] = useState(DEFAULT_TEAM_SORT);

  const rows = useMemo((): TeamTableRow[] => {
    const memberRows = sortTeamMembers(members, sort).map(
      (member): TeamTableRow => ({
        key: member.userId,
        label: memberName(member),
        counts: member,
        former: member.former,
        isTotal: false,
        onClick: () => onSelectMember(member.userId),
      }),
    );

    if (!total || memberRows.length === 0) return memberRows;

    return [
      ...memberRows,
      { key: 'total', label: TOTAL_ROW_LABEL, counts: total, former: false, isTotal: true },
    ];
  }, [members, total, sort, onSelectMember]);

  return (
    <Table
      columns={columns}
      rows={rows}
      size="sm"
      sortKey={sort.key}
      sortDirection={sort.direction}
      onSort={(key) => setSort((current) => nextTeamSort(current, key as TeamSortKey))}
      isLoading={isLoading}
      skeletonRowCount={6}
      minTableWidth={getTableMinWidth(columns)}
      emptyState={
        <EmptyState variant="tertiary" role="status" title="No team members in this period." />
      }
    />
  );
};
